import { LegalPageLayout } from './LegalPageLayout';
import { legal } from './legalConfig';

export function DataDeletionPage() {
  const subject = encodeURIComponent('Erasure Request — Hire It Now');
  const body = encodeURIComponent(
    'Please delete my personal data from the Hire It Now platform.\n\nName: \nEmail address: \nAny additional details: ',
  );
  const mailtoHref = `mailto:${legal.privacyEmail}?subject=${subject}&body=${body}`;

  return (
    <LegalPageLayout title="Data Deletion Request">
      <header className="legal-header">
        <h1>Request Data Deletion</h1>
        <div className="legal-meta">
          <span className="legal-meta-badge">Right to Erasure — UK GDPR Article 17</span>
        </div>
      </header>

      <div className="legal-section">
        <p>
          Under UK GDPR Article 17, you have the right to request that we delete your personal
          data. This page explains when this right applies, what we will action, and how to
          submit a request.
        </p>
      </div>

      <div className="legal-section">
        <h2>When does this right apply?</h2>
        <p>You can request erasure of your data when:</p>
        <ul>
          <li>The data is no longer necessary for the purpose it was collected</li>
          <li>You withdraw consent and there is no other lawful basis for processing</li>
          <li>You object to processing based on legitimate interest and we have no overriding grounds</li>
          <li>The data has been unlawfully processed</li>
          <li>You received a Hire It Now invite email and do not wish to be on our platform</li>
        </ul>
        <p>
          Note: the right to erasure is not absolute. We may retain some data where required
          by law (e.g. consent audit records, which are a legal obligation) or where we have a
          legitimate overriding interest.
        </p>
      </div>

      <div className="legal-section">
        <h2>What we will action</h2>
        <p>
          When we receive and verify a valid erasure request, we will:
        </p>
        <ul>
          <li>Delete or anonymise your account record</li>
          <li>Soft-delete all contact (Person) records associated with your account</li>
          <li>Revoke any open invite tokens linked to your email</li>
          <li>Remove consent records (T&Cs, Privacy Policy, marketing)</li>
          <li>Confirm completion with a deletion receipt within one calendar month</li>
        </ul>
        <p>
          A small anonymised shell record may be retained to preserve referential integrity in
          operational audit tables. This record will contain no identifiable information.
        </p>
      </div>

      <div className="legal-section">
        <h2>How to submit a request</h2>
        <p>
          Email us at{' '}
          <a href={mailtoHref} className="legal-link">
            {legal.privacyEmail}
          </a>{' '}
          with the subject line <strong>"Erasure Request"</strong> and include:
        </p>
        <ul>
          <li>Your full name</li>
          <li>The email address associated with your Hire It Now account or invite</li>
          <li>Any additional context to help us locate your record</li>
        </ul>
        <p>
          We will respond within <strong>one calendar month</strong>. If your request is
          complex or we receive a high volume of requests, we may extend this by a further two
          months and will notify you accordingly.
        </p>
        <p>
          We may ask you to verify your identity before processing the request to ensure we
          are deleting the correct data.
        </p>
        <p style={{ marginTop: '24px' }}>
          <a href={mailtoHref} className="legal-link" style={{ fontWeight: 600 }}>
            Send erasure request by email →
          </a>
        </p>
      </div>

      <div className="legal-section">
        <h2>Complaints</h2>
        <p>
          If you are unhappy with how we handle your request, you have the right to lodge a
          complaint with the Information Commissioner's Office (ICO):
        </p>
        <ul>
          <li>
            Website:{' '}
            <a href={legal.icoUrl} className="legal-link" target="_blank" rel="noopener noreferrer">
              ico.org.uk
            </a>
          </li>
          <li>Phone: 0303 123 1113</li>
        </ul>
      </div>

      <div className="legal-section">
        <h2>Contact</h2>
        <div className="legal-footer">
          <p><strong>{legal.companyName}</strong></p>
          <p>{legal.companyAddress}</p>
          <p>
            <a href={`mailto:${legal.privacyEmail}`} className="legal-link">
              {legal.privacyEmail}
            </a>
          </p>
          <p>Company No. {legal.companyNumber} (England and Wales)</p>
          <p style={{ marginTop: '12px' }}>
            <a href="/privacy" className="legal-link">Privacy Policy</a>
            {' · '}
            <a href="/terms" className="legal-link">Terms of Service</a>
          </p>
        </div>
      </div>
    </LegalPageLayout>
  );
}
